import React from "react";
import styled from "styled-components";
import HugeIcon from "src/components/HugeIcon";
import { colors } from "src/theme/colors";
import { line } from "src/theme/ui";

/**
 * SettingRow — the settings canvas's most repeated shape.
 *
 * Ported from football: a titled line with an optional description on the
 * left and its controls on the right, as one row of a divided card list — a
 * two-factor method, a notification channel, a connected account.
 *
 * It replaces what the settings sections were built from here: nested antd
 * Rows and Cols, every one of them `justify="center"`, so a method's name, its
 * description, the words "Set as primary method", a switch and a button were
 * each centred inside their own column and nothing on the page shared an
 * edge with anything else. A row is a row: one left edge for the labels, one
 * right edge for the controls.
 */
const Row = styled.div<{ $flush?: boolean }>`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  border-bottom: 1px solid ${line.hairline};

  &:last-child {
    border-bottom: 0;
  }

  ${({ $flush }) => ($flush ? "border-bottom: 0;" : "")}

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Lead = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  color: ${colors.gray[500]};
`;

const Body = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.div`
  font-size: 12.5px;
  font-weight: 560;
  color: ${colors.gray[900]};
  overflow-wrap: anywhere;
`;

const Description = styled.div`
  margin-top: 3px;
  font-size: 11.5px;
  line-height: 1.45;
  color: ${colors.gray[500]};
`;

const Controls = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  gap: 10px;

  @media (max-width: 640px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

export interface SettingRowProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** A leading glyph or image. */
  lead?: React.ReactNode;
  /** A HugeIcons glyph, drawn at 18px in muted ink. */
  icon?: any;
  control?: React.ReactNode;
  /** Drop the row's own hairline, when the list draws its own. */
  flush?: boolean;
  className?: string;
}

const SettingRow: React.FC<SettingRowProps> = ({
  title,
  description,
  lead,
  icon,
  control,
  flush,
  className,
}) => (
  <Row $flush={flush} className={className}>
    {(lead || icon) && (
      <Lead>{lead ?? <HugeIcon icon={icon} size={18} />}</Lead>
    )}
    <Body>
      <Title>{title}</Title>
      {description && <Description>{description}</Description>}
    </Body>
    {control && <Controls>{control}</Controls>}
  </Row>
);

export default SettingRow;
